/**
 * Última lista de laudos aberta (aba + query), guardada na sessão.
 *
 * O detalhe (`MedicalReport.vue`) volta para a lista por caminhos fixos e sem
 * query: o breadcrumb aponta para /laudos-medicos/laudos-pendentes e os
 * `$router.push` depois de editar, finalizar, devolver ou excluir também. Como
 * o detalhe não muda, a rota da lista redireciona quem chega dele sem query
 * para a última lista salva aqui (`resolveReturnRedirect`, usado no
 * `beforeEnter` de /laudos-medicos/:tab).
 *
 * Sem dependência de React: o router (JS) importa este arquivo.
 */

import { REPORTS_TABS, type ListQuery, type ReportsTab } from '../types'

export const LIST_LOCATION_KEY = 'medicalReports:listLocation'

const LIST_PREFIX = '/laudos-medicos/'
const DETAIL_PREFIX = '/laudo-medico/'

export interface ListLocation {
  tab: ReportsTab
  query: ListQuery
}

interface RouteLike {
  path?: string | null
  query?: Record<string, unknown> | null
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

function defaultStorage(): StorageLike | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

export function isTab(value: unknown): value is ReportsTab {
  return REPORTS_TABS.includes(value as ReportsTab)
}

/** Só chaves com valor string não vazio (o resto não vai para a URL). */
export function cleanQuery(
  query: Record<string, unknown> | null | undefined
): ListQuery {
  const clean: ListQuery = {}
  Object.entries(query ?? {}).forEach(([key, value]) => {
    const raw = Array.isArray(value) ? value[0] : value
    if (typeof raw === 'string' && raw !== '') clean[key] = raw
  })
  return clean
}

export function saveListLocation(
  tab: string | null | undefined,
  query: Record<string, unknown> | null | undefined,
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage || !isTab(tab)) return
  try {
    storage.setItem(
      LIST_LOCATION_KEY,
      JSON.stringify({ tab, query: cleanQuery(query) })
    )
  } catch {
    // storage cheio ou bloqueado: perde só o retorno à lista filtrada
  }
}

export function loadListLocation(
  storage: StorageLike | null = defaultStorage()
): ListLocation | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(LIST_LOCATION_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    if (!parsed || !isTab(parsed.tab)) return null
    return { tab: parsed.tab, query: cleanQuery(parsed.query) }
  } catch {
    return null
  }
}

function sameQuery(a: ListQuery, b: ListQuery): boolean {
  const keys = Object.keys(a)
  return (
    keys.length === Object.keys(b).length &&
    keys.every(key => a[key] === b[key])
  )
}

/**
 * Para onde mandar quem entra em /laudos-medicos/* — `null` = seguir.
 *
 * Só redireciona quando a origem é o detalhe (/laudo-medico/*) e o destino
 * chega sem query: é a volta pela tela. Destino com query (link, voltar do
 * navegador) ou entrada por outro lugar (menu) seguem como vieram. Destino
 * igual ao salvo não redireciona — é o que evita laço.
 */
export function resolveReturnRedirect(
  to: RouteLike,
  from: RouteLike | null | undefined,
  storage: StorageLike | null = defaultStorage()
): { path: string; query: ListQuery } | null {
  const toPath = to?.path ?? ''
  const fromPath = from?.path ?? ''
  if (!toPath.startsWith(LIST_PREFIX)) return null
  if (!fromPath.startsWith(DETAIL_PREFIX)) return null
  if (Object.keys(to.query ?? {}).length > 0) return null

  const saved = loadListLocation(storage)
  if (!saved) return null

  const path = `${LIST_PREFIX}${saved.tab}`
  if (path === toPath.replace(/\/+$/, '') && sameQuery(saved.query, {})) {
    return null
  }

  return { path, query: saved.query }
}
